// src/pages/Knowledge.js
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { updateBusiness } from '../lib/airtable';
import Nav from '../components/Nav';
import Sidebar from '../components/Sidebar';

export default function Knowledge() {
  const { business, refreshBusiness } = useAuth();
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (business) {
      setContent(business['AI Knowledge'] || '');
    }
  }, [business]);

  async function handleSave() {
    if (!business?.id) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await updateBusiness(business.id, { 'AI Knowledge': content });
      if (refreshBusiness) await refreshBusiness();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Could not save. Please try again or check your connection.');
    } finally {
      setSaving(false);
    }
  }

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="page">
      <Nav />
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">

          <div className="fade-up" style={{ marginBottom: 8 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, marginBottom: 4 }}>AI Receptionist Knowledge</h1>
            <p style={{ color: 'var(--muted)', fontSize: 15 }}>
              Train your AI receptionist on policies, FAQs, and rules specific to your business.
            </p>
          </div>

          {/* What this is */}
          <div className="alert alert-info" style={{ marginBottom: 24, marginTop: 24 }}>
            <strong>💡 How this works:</strong> Anything you write here, your AI receptionist will know about and reference during calls.
            Use this for cancellation policies, deposit rules, service area limits, FAQs your customers ask, or anything else
            you want the AI to handle confidently without transferring to you.
          </div>

          {/* Editor */}
          <div className="card fade-up-2" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label className="form-label" style={{ marginBottom: 0, fontSize: 14 }}>
                Knowledge & policies your AI should know
              </label>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{wordCount} words</span>
            </div>
            <textarea
              className="form-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={`Examples of what to add:

CANCELLATION POLICY
• 24-hour notice required for cancellations
• Same-day cancellations charged 50% fee
• Reschedules are free with 24h notice

PAYMENT
• We accept cash, Zelle, and Venmo
• Deposit required for first-time customers: $50
• Payment due at time of service

SERVICE AREA
• We cover all of Tomball, Spring, and The Woodlands
• Outside of 20 miles: $25 travel fee applies

COMMON QUESTIONS
• Yes, we bring our own supplies
• Yes, we're licensed and insured
• Pets are fine, just let us know in advance`}
              style={{ minHeight: 360, fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.75 }}
            />
          </div>

          {/* Status row */}
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          {saved && <div className="alert alert-success" style={{ marginBottom: 16 }}>✓ Saved. Your AI receptionist will use this knowledge on the next call (within 15 min).</div>}

          {/* Save */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
              {saving ? <span className="spinner" /> : 'Save knowledge →'}
            </button>
          </div>

          {/* Tips */}
          <div className="card" style={{ background: 'var(--surface)', marginTop: 32 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, marginBottom: 12 }}>💪 Tips for the best results</h3>
            <ul style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.9, paddingLeft: 18, margin: 0 }}>
              <li><strong>Be specific.</strong> "Deposit is $50" works better than "We require a deposit."</li>
              <li><strong>Use plain English.</strong> Write the way you'd explain it to a new employee.</li>
              <li><strong>Group similar info.</strong> Use headings like CANCELLATION POLICY, PAYMENT, etc.</li>
              <li><strong>Keep it focused.</strong> Add the questions customers actually ask, not everything about your business.</li>
              <li><strong>Update as you learn.</strong> When a caller asks something new, add it here so your AI knows next time.</li>
            </ul>
          </div>

        </main>
      </div>
    </div>
  );
}
